import { createIncident, type IncidentCreate } from "@/api/incident";
import { useTeamStore } from "@/app/store/useTeamStore";
import { NoTeamSelected } from "@/shared/components/NoTeamSelected";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Textarea } from "@/shared/components/ui/textarea";
import { useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { useState } from "react";

interface FormState {
  title: string;
  message: string;
}

const INITIAL_FORM: FormState = {
  title: "",
  message: "",
};

export function ManualAlertTriggerPage() {
  const { selectedTeam } = useTeamStore();
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [lastCreated, setLastCreated] = useState<{ id: number; title: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (data: IncidentCreate) => {
      if (!selectedTeam) throw new Error("No team selected");
      return createIncident(selectedTeam.id, data);
    },
    onSuccess: (res) => {
      setLastCreated({ id: res.id, title: res.title });
      setForm(INITIAL_FORM);
    },
  });

  if (!selectedTeam) return <NoTeamSelected />;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    const payload: IncidentCreate = {
      title: form.title.trim(),
      source: "manual",
      ...(form.message.trim() && { message: form.message.trim() }),
    };
    mutation.mutate(payload);
  };

  const isSubmitting = mutation.isPending;

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-xl font-bold">수동 알람 트리거</h1>
        <p className="text-sm text-muted-foreground">{selectedTeam.name}</p>
      </div>

      {lastCreated && (
        <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 p-4 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200">
          <CheckCircle2 className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-medium">인시던트가 생성되었습니다</p>
            <p className="text-sm opacity-80">#{lastCreated.id} · {lastCreated.title}</p>
          </div>
          <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setLastCreated(null)}>
            닫기
          </Button>
        </div>
      )}

      {mutation.isError && (
        <div className="flex items-center gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p className="text-sm">
            {mutation.error instanceof Error ? mutation.error.message : "인시던트 생성에 실패했습니다."}
          </p>
        </div>
      )}

      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle className="text-base">인시던트 생성</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="title">
                제목 <span className="text-destructive">*</span>
              </Label>
              <Input
                id="title"
                placeholder="인시던트 제목을 입력하세요"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                disabled={isSubmitting}
                maxLength={256}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="message">
                메시지 <span className="text-xs font-normal text-muted-foreground">(선택)</span>
              </Label>
              <Textarea
                id="message"
                placeholder="상세 내용을 입력하세요"
                value={form.message}
                onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                disabled={isSubmitting}
                rows={4}
              />
            </div>

            <Button type="submit" className="w-full" disabled={isSubmitting || !form.title.trim()}>
              <AlertTriangle className="mr-2 h-4 w-4" />
              {isSubmitting ? "생성 중..." : "생성하기"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
